import type { PageFragment } from '@mnd-apps/public/wp-generated/types';
import { useMediaQuery } from '@mnd-frontend/hooks';
import { BREAKPOINTS } from '@mnd-frontend/ui/shared/tokens';
import Image from 'next/image';
import styled from 'styled-components';

const IMAGE_BREAKPOINT = BREAKPOINTS.SM;

const BackgroundImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -2;
`;

const MobileImage = styled(BackgroundImage)`
  @media (min-width: ${IMAGE_BREAKPOINT}px) {
    display: none;
  }
`;

const LargeDeviceImage = styled(BackgroundImage)`
  display: none;
  
  @media (min-width: ${IMAGE_BREAKPOINT}px) {
    display: block;
  }
`;

const HeroImage = ({
  data,
}: {
  data: NonNullable<PageFragment['hero']>['hero'] | NonNullable<PageFragment['hero']>['heroTrial'];
}) => {
  const mobileImage = data && 'mobileImage' in data ? data?.mobileImage : false;
  const isMobile = useMediaQuery(`@media (max-width: ${IMAGE_BREAKPOINT - 1}px)`);

  if (!mobileImage) {
    if (!data?.image) return null;

    return (
      <BackgroundImage
        src={data?.image?.node?.mediaItemUrl ?? ''}
        alt={data?.image?.node?.altText ?? ''}
        width={data?.image?.node.mediaDetails?.width ?? 1920}
        height={data?.image?.node.mediaDetails?.height ?? 1080}
        priority
      />
    );
  }

  return (
    <>
      {/**
       * To avoid image switches during hydration,
       * both hero images need to be rendered despite of device sizes and control display via css.
       */}
      <MobileImage
        src={mobileImage?.node?.mediaItemUrl ?? ''}
        alt={mobileImage?.node?.altText ?? ''}
        width={mobileImage?.node?.mediaDetails?.width ?? 768}
        height={mobileImage?.node?.mediaDetails?.height ?? 1024}
        priority
      />
      {
        /** Hide this element from DOM for mobile in CSR. */
        !isMobile && (
          <LargeDeviceImage
            src={data?.image?.node?.mediaItemUrl ?? ''}
            alt={data?.image?.node?.altText ?? ''}
            width={data?.image?.node.mediaDetails?.width ?? 1920}
            height={data?.image?.node.mediaDetails?.height ?? 1080}
            priority
          />
        )
      }
    </>
  );
};

export default HeroImage;
